<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed, reactive, ref } from 'vue'
import { useMutation, useQuery } from '@vue/apollo-composable'
import { CreateStep, Mutation, PermissionOrganization, Query } from '@/generated/graphql'
import gql from 'graphql-tag'
import { useRouter } from 'vue-router'
import useVuelidate from '@vuelidate/core'
import { v4 } from 'uuid'
import Breadcrumb from '@/components/Breadcrumb.vue'
import DataTable from '@/components/dataTable/DataTable.vue'
import DataTableSearch from '@/components/dataTable/DataTableSearch.vue'
import DataTableCreateButton from '@/components/dataTable/DataTableCreateButton.vue'
import UpsertDialog from '@/components/dialogs/UpsertDialog.vue'
import TextField from '@/components/input/TextField.vue'
import TextAreaField from '@/components/input/TextAreaField.vue'
import StepTypePicker from '@/components/input/StepTypePicker.vue'
import DurationField from '@/components/input/DurationField.vue'
import { ComponentExposed } from 'vue-component-type-helpers'
import { createStepValidationRules } from '@/validation/step'
import useAuthorization from '@/composables/useAuthorization'

const { t } = useI18n()
const router = useRouter()
const { hasOrgAuthz } = useAuthorization()

const activeTab = ref<'steps' | 'modules' | 'bops'>('steps')

const fetchQuery = useQuery<Query>(gql`
  query LibraryViewData {
    product {
      librarySteps {
        id
        name
        instructionText
        updatedAt
        usageCount
      }
    }
  }
`)
type LibraryStep = Query['product']['librarySteps'][number]
const items = computed<LibraryStep[]>(() => fetchQuery.result.value?.product.librarySteps ?? [])

const search = ref('')
const headers = [
  { key: 'name', title: t('entity.step.field.name') },
  { key: 'instructionText', title: t('entity.step.field.instructionText'), sortable: false },
  { key: 'updatedAt', title: t('view.organization.library.lastModified') },
  { key: 'usageCount', title: t('view.organization.library.usage'), width: 100 },
  { key: 'actions', width: 120, sortable: false, align: 'end' },
]
const sort = [{ key: 'name', order: 'asc' as const }]

const onRowClick = (_: unknown, row: { item: LibraryStep }) =>
  router.push({ name: 'libraryStep', params: { stepId: row.item.id } })

function truncate(text: string | null | undefined, max = 80): string {
  if (!text) return ''
  return text.length > max ? `${text.slice(0, max)}…` : text
}
function formatDate(value: string): string {
  return new Date(value).toLocaleDateString()
}

type CreateModel = Partial<CreateStep>
const createModel = reactive<CreateModel>({})
const createValidation = useVuelidate<CreateModel>(createStepValidationRules, createModel)
const createMutation = useMutation<Mutation, { command: CreateStep }>(gql`
  mutation LibraryViewCreateStep($command: CreateStep!) {
    product {
      createStep(command: $command) {
        id
      }
    }
  }
`)
const createDialog = ref<ComponentExposed<typeof UpsertDialog<() => Promise<unknown>>>>()
const doCreate = async () => {
  const newStepId = v4()
  createModel.id = newStepId
  createModel.library = true
  createModel.toolIds = []
  createModel.name = undefined
  createModel.stepTypeId = undefined
  createModel.instructionText = undefined
  createModel.durationInSeconds = 0
  await createDialog.value?.open(async () => {
    await createMutation.mutate({ command: createModel as CreateStep })
    // Jump straight into the new step's details so it can be filled in immediately.
    await router.push({ name: 'libraryStep', params: { stepId: newStepId } })
  })
}
</script>

<template>
  <breadcrumb />
  <h1>{{ t('view.organization.library.title') }}</h1>

  <v-tabs v-model="activeTab" class="mb-4">
    <v-tab value="steps">{{ t('entity.step.plural') }}</v-tab>
    <v-tab value="modules" disabled>{{ t('entity.module.plural') }}</v-tab>
    <v-tab value="bops" disabled>{{ t('entity.bop.plural') }}</v-tab>
  </v-tabs>

  <template v-if="activeTab === 'steps'">
    <data-table
      :items="items"
      :headers="headers"
      :loading="fetchQuery.loading.value"
      :sort-by="sort"
      :search="search"
      density="comfortable"
      @click:row="onRowClick"
    >
      <template #header.actions>
        <data-table-search v-model="search" />
        <data-table-create-button
          v-if="hasOrgAuthz(PermissionOrganization.OrgProductWrite)"
          @click="doCreate"
        />
      </template>
      <template #item.instructionText="{ item }: { item: LibraryStep }">
        {{ truncate(item.instructionText) }}
      </template>
      <template #item.updatedAt="{ item }: { item: LibraryStep }">
        {{ formatDate(item.updatedAt) }}
      </template>
      <template #no-data>
        <div class="py-4">
          <p class="mb-3">{{ t('view.organization.library.emptySteps') }}</p>
          <v-btn
            v-if="hasOrgAuthz(PermissionOrganization.OrgProductWrite)"
            color="primary"
            variant="elevated"
            prepend-icon="add"
            @click="doCreate"
          >
            {{ t('view.organization.library.createStep') }}
          </v-btn>
        </div>
      </template>
    </data-table>
  </template>

  <upsert-dialog
    ref="createDialog"
    type="create"
    :entity="t('view.organization.library.libraryStep')"
    :validation="createValidation"
  >
    <text-field
      v-model="createModel.name"
      :label="t('entity.step.field.name')"
      required
      :validation="createValidation.name"
    />
    <step-type-picker
      v-model="createModel.stepTypeId"
      :label="t('entity.step.field.stepType')"
      required
      :validation="createValidation.stepTypeId"
    />
    <duration-field
      v-model="createModel.durationInSeconds"
      :label="t('entity.step.field.durationInSeconds')"
      required
      :validation="createValidation.durationInSeconds"
    />
    <text-area-field
      v-model="createModel.instructionText"
      :label="t('entity.step.field.instructionText')"
      :validation="createValidation.instructionText"
      markdown
    />
  </upsert-dialog>
</template>

<style scoped lang="scss"></style>
